import type { PaymentSession } from '@capsico/contracts';
import { BRAND_500 } from '../tokens';

/*
 * Handing the customer to a payment gateway (W11). No secrets live here: Razorpay's key id is
 * public by design and form fields are signed by the server. Whatever comes back is only passed
 * on to the server, which verifies it with the gateway itself.
 */

const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

interface RazorpayOptions {
  key: string;
  order_id: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  prefill: { contact?: string; method?: string };
  theme: { color: string };
  handler: (response: Record<string, string>) => void;
  modal: { ondismiss: () => void };
}

interface RazorpayInstance {
  open: () => void;
  on: (event: 'payment.failed', handler: () => void) => void;
}

type RazorpayConstructor = new (options: RazorpayOptions) => RazorpayInstance;

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor;
  }
}

let loading: Promise<RazorpayConstructor> | null = null;

/** Loads Razorpay Checkout once, on first use (never on app start). */
export function loadRazorpay(): Promise<RazorpayConstructor> {
  if (window.Razorpay) return Promise.resolve(window.Razorpay);
  loading ??= new Promise<RazorpayConstructor>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = RAZORPAY_SRC;
    script.async = true;
    script.onload = () => {
      if (window.Razorpay) resolve(window.Razorpay);
      else reject(new Error('Razorpay did not load'));
    };
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error('Razorpay did not load'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/**
 * Opens the Razorpay sheet. Resolves with the handler fields on success, or null when the
 * customer closed it (the order keeps waiting and can be paid again).
 */
export async function openRazorpay(
  session: Extract<PaymentSession, { kind: 'razorpay_checkout' }>,
): Promise<Record<string, string> | null> {
  const Razorpay = await loadRazorpay();
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: Record<string, string> | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const checkout = new Razorpay({
      key: session.keyId,
      order_id: session.orderId,
      amount: session.amountMinor,
      currency: session.currency,
      name: session.name,
      description: session.description,
      prefill: {
        ...(session.prefillContact ? { contact: session.prefillContact } : {}),
        method: session.method,
      },
      theme: { color: BRAND_500 },
      handler: (response) => finish(response),
      modal: { ondismiss: () => finish(null) },
    });
    checkout.open();
  });
}

/** Builds a hidden form with the signed fields and submits it (eSewa, IME Pay). */
export function postForm(action: string, fields: Record<string, string>): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  form.style.display = 'none';
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

/** Every query parameter on the return URL, as plain strings (bounded like the server expects). */
export function queryParams(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(search)) {
    if (key.length > 64) continue;
    out[key] = value.slice(0, 4000);
  }
  return out;
}

/** Saves a downloaded file (invoice PDF) through a temporary link. */
export function saveFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
